import type { apiTypes } from '@cmg/common';
import { ApplicationError, InterstitialPage, urlUtil } from '@cmg/common';
import React from 'react';
import { useLocation } from 'react-router-dom';

import identityApiClient from '../../../common/api/identityApiClient';
import { getAppSettings } from '../../../common/config/appSettings';
import routeFactory from '../../../common/util/routeFactory';
import type { ErrorCode } from './constants';
import { errorCodeMap, signInFailedTitle } from './constants';

type ErrorQuery = {
  /** One of the application's own codes. */
  errorCode?: apiTypes.ServiceErrorCode | ErrorCode | string;
  /** IdentityServer's message-store key, opaque and resolvable only server-side. */
  errorId?: string;
};

/**
 * Displays a centered error display letting the user know why they landed here
 * and allowing them to go back to the ECM homepage.
 *
 * An `errorId` is asked of the server — it is a message-store key and means nothing here. An
 * `errorCode` is one of the application's own and is rendered from `errorCodeMap`.
 */
const ErrorRoute = () => {
  const location = useLocation();

  const appSettings = getAppSettings();
  const { errorCode, errorId } = urlUtil.queryParse<ErrorQuery>(location.search);

  // Tagged with the id it belongs to, so a result never outlives the id that produced it.
  const [resolved, setResolved] = React.useState<{
    id: string;
    details?: { title: string; message: string };
  }>();

  const isResolving = !!errorId && resolved?.id !== errorId;

  // Whatever the URL carried is what the screen keys its text on.
  const displayedCode = errorId ?? errorCode;

  React.useEffect(() => {
    if (!errorId) {
      return;
    }

    identityApiClient
      .fetchErrorInfo({ errorId })
      .then(response => {
        // An id that resolves to nothing is a 404, which leaves the details unset and renders the
        // generic screen rather than an error the server never recorded.
        setResolved({
          id: errorId,
          details: response.ok
            ? {
                title: signInFailedTitle,
                // The description is what a person can read. The raw error value (`invalid_scope`
                // and friends) and the requestId stay in the response for diagnostics, unshown.
                message: response.data.description ?? response.data.error,
              }
            : undefined,
        });
      })
      // A failed call must land on the generic screen; leaving it unset spins forever.
      .catch(() => setResolved({ id: errorId, details: undefined }));
  }, [errorId]);

  if (isResolving) {
    return <InterstitialPage />;
  }

  return (
    <div data-testid="error-page">
      <ApplicationError
        errorCode={displayedCode}
        customErrorDetails={
          resolved?.details ? { ...errorCodeMap, [resolved.id]: resolved.details } : errorCodeMap
        }
        returnUrl={`${appSettings.client.basename}${routeFactory.root.getUrlPath()}`}
      />
    </div>
  );
};

export default ErrorRoute;
